import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Archive, ArrowLeft, ArrowRight, Calendar, CheckCheck, CheckCircle2, ChevronDown, ChevronUp,
  Cog, FileText, Layers, Palette, Pencil, Phone, Printer, RefreshCw, RotateCw, Send, Tag,
  Truck, User,
} from 'lucide-react';

import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { needsPlate } from '@/lib/plate';
import { openAuthedFile } from '@/lib/fileDownload';
import { useAssignMachine } from '@/hooks/useAssignMachine';
import { JobCardAttachments } from '@/components/attachments';
import MachinePicker from '@/components/phone/MachinePicker';
import {
  BottomSheet, ConfirmDialog, IconButton, ScreenLoader, Spinner, StatusPill, TopBar,
} from '@/components/phone/primitives';
import { Colors, tone } from '@/components/phone/theme';

/**
 * One job card, the way the phone shows it.
 *
 * The desktop supervisor detail page splits the same facts across a rail and two
 * panes; this is the app's single column, in the app's order — where the work
 * stands, then the buttons that move it, then the reference material a
 * supervisor reads once.
 *
 * Sending the batch on sits at the top, above the specifications and the
 * timeline: it is the one thing the card is opened to do, and putting it under
 * the reference material meant a scroll on every visit.
 *
 * Mirrors `mobile-frontend/app/job-card/[id].tsx` and its sections.
 */

const STOCK_IN_STAGE_KEY = 'stock_in';

/**
 * Where the work stands — `production_status`, derived from the line item's
 * batches, which is what the desktop reads.
 *
 * The card's own `status` is the *specification* one: draft / in_progress /
 * complete, counted off how many form fields are filled, most of them blank on
 * purpose. Reading it here is what showed a finished job as IN PROGRESS while
 * the desktop called the same card Completed.
 */
const STATUS_LABELS = {
  pending: 'Not started',
  design: 'With the designer',
  in_production: 'In production',
  completed: 'Completed',
  delivered: 'Delivered',
  stocked: 'In stock',
  split: 'Split',
};

const STATUS_ICONS = {
  completed: CheckCircle2, delivered: CheckCheck, stocked: Archive,
  in_production: RotateCw, design: Palette, pending: Layers, split: Layers,
};

const STATUS_COLORS = {
  pending: Colors.status.warning, design: Colors.status.warning,
  in_production: Colors.status.info, completed: Colors.status.success,
  delivered: Colors.status.success, stocked: Colors.status.success,
  split: Colors.status.neutral,
};

const ASSIGNMENT_TYPES = [
  { key: 'binding_folding', label: 'Binding/Folding' },
  { key: 'lamination', label: 'Lamination' },
  { key: 'uv_printing', label: 'UV Printing' },
  { key: 'foil_printing', label: 'Foil Printing' },
  { key: 'other', label: 'Other' },
];

const hasRole = (user, ...roles) => {
  const held = (user?.roles?.length ? user.roles : [user?.role])
    .filter(Boolean)
    .map((r) => (r === 'machine_man' ? 'machine' : r));
  return roles.some((r) => held.includes(r));
};

/** `production_status` where the server sends it; older payloads have none. */
const productionStatus = (jobCard) =>
  jobCard.production_status || (jobCard.batches?.length ? 'in_production' : 'pending');

/** One fact in the order grid: a small label over its value, two to a row. */
function Fact({ icon: Icon, label, value, wide }) {
  return (
    <div className={`py-2 pr-2 ${wide ? 'w-full' : 'w-1/2'}`}>
      <p className="flex items-center gap-1">
        <Icon className="h-3 w-3 shrink-0" style={{ color: Colors.accent.DEFAULT }} />
        <span className="text-[11px] font-medium uppercase tracking-[0.4px]" style={{ color: Colors.text.secondary }}>
          {label}
        </span>
      </p>
      <p className="mt-0.5 line-clamp-2 text-[13px] font-semibold" style={{ color: Colors.text.primary }}>{value}</p>
    </div>
  );
}

function Section({ title, children, collapsible, expanded, onToggle }) {
  return (
    <section className="mb-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        {collapsible ? (
          <button type="button" onClick={onToggle} className="flex flex-1 items-center justify-between gap-2">
            <span className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>{title}</span>
            {expanded
              ? <ChevronUp className="h-[18px] w-[18px]" style={{ color: Colors.text.secondary }} />
              : <ChevronDown className="h-[18px] w-[18px]" style={{ color: Colors.text.secondary }} />}
          </button>
        ) : (
          <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>{title}</h2>
        )}
      </div>
      {(!collapsible || expanded) && children}
    </section>
  );
}

function Panel({ children, className = '', style }) {
  return (
    <div
      className={`rounded-[14px] border p-4 ${className}`}
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default, ...style }}
    >
      {children}
    </div>
  );
}

/** An outline button: the label says where the job is going. */
function MoveButton({ icon: Icon, iconRight, label, onClick, disabled, testID }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled}
      className="flex min-w-0 flex-1 items-center justify-center gap-1 rounded-[10px] border px-2 py-2.5 disabled:opacity-50"
      style={{ borderColor: Colors.border.default }}
    >
      {!iconRight && !!Icon && <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.text.secondary }} />}
      <span className="truncate text-[11px] font-medium" style={{ color: Colors.text.primary }}>{label}</span>
      {iconRight && !!Icon && <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.text.secondary }} />}
    </button>
  );
}

export default function SupervisorJobCardPage() {
  const { id: jobCardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [jobCard, setJobCard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stage movement. 'short' records the pcs that did not make it and moves the
  // batch on whole; 'partial' is the older meaning — the rest stays behind as
  // its own batch.
  const [moveDialog, setMoveDialog] = useState(null);
  const [moveQtyMode, setMoveQtyMode] = useState('full');
  const [moveQty, setMoveQty] = useState('');
  const [moveBackReason, setMoveBackReason] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  // Stock In, on a replenishment run
  const [stockConfirm, setStockConfirm] = useState(null);

  // Plate
  const [plateSheet, setPlateSheet] = useState(false);
  const [plateVendor, setPlateVendor] = useState('');
  const [orderingPlate, setOrderingPlate] = useState(false);
  const [receivingPlate, setReceivingPlate] = useState(false);

  // Vendor
  const [vendorSheet, setVendorSheet] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorForm, setVendorForm] = useState({
    vendor_id: '', assignment_type: 'binding_folding', special_instructions: '', expected_return_date: '',
  });
  const [sendingVendor, setSendingVendor] = useState(false);

  // Close the order out
  const [completeConfirm, setCompleteConfirm] = useState(false);
  const [completing, setCompleting] = useState(false);
  const completingRef = useRef(false);

  // Remarks
  const [remarkText, setRemarkText] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);

  const [printing, setPrinting] = useState(false);
  const [specsExpanded, setSpecsExpanded] = useState(true);
  const [timelineExpanded, setTimelineExpanded] = useState(true);
  const [remarksExpanded, setRemarksExpanded] = useState(true);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);

  // Roles as a union, and by the names the backend actually uses.
  const canPrint = !hasRole(user, 'machine');
  const canManagePlate = hasRole(user, 'admin', 'designer', 'supervisor');
  const canMoveStages = hasRole(user, 'admin', 'supervisor');
  const isSupervisor = canMoveStages;
  const canCompleteOrder = hasRole(user, 'admin', 'supervisor', 'dispatcher');

  const fetchJobCard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/job-cards/${jobCardId}`);
      setJobCard(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load job card');
    }
    setLoading(false);
  }, [jobCardId]);

  useEffect(() => { if (jobCardId) fetchJobCard(); }, [jobCardId, fetchJobCard]);

  const assignment = useAssignMachine((outcome) => {
    if (outcome.ok) { toast.success(outcome.message); fetchJobCard(); } else toast.error(outcome.message);
  });

  const stages = jobCard?.production_stages || [];
  const currentStageIndex = jobCard?.current_stage_index || 0;
  const currentStage = stages[currentStageIndex];
  const batches = jobCard?.batches || [];

  // A replenishment run ends at Stock In instead of Dispatch, so nothing behind
  // it closes the last stage — the store counting the goods in is what finishes
  // the run and what credits the catalogue's shelf.
  const endsAtStockIn = stages[stages.length - 1]?.stage_key === STOCK_IN_STAGE_KEY;

  const openMoveDialog = (batchId, direction, batchQty, fromStage, toStage) => {
    setMoveDialog({ batchId, direction, batchQty, fromStage, toStage });
    setMoveQtyMode('full');
    setMoveQty(String(batchQty));
    setMoveBackReason('');
  };

  const handleMoveForward = (batch) => {
    const batchQty = batch?.quantity || jobCard?.quantity || 0;
    const idx = batch?.current_stage_index ?? currentStageIndex;
    openMoveDialog(
      batch?.batch_id || batch?.id, 'forward', batchQty,
      stages[idx]?.stage_name || 'Current', stages[idx + 1]?.stage_name || 'Completed',
    );
  };

  const handleMoveBackward = (batch, fromStage, toStage) => {
    const batchQty = batch?.quantity || jobCard?.quantity || 0;
    openMoveDialog(batch?.batch_id || batch?.id, 'backward', batchQty, fromStage || 'Current', toStage || 'Previous');
  };

  const confirmMove = async () => {
    if (!moveDialog) return;
    const qty = moveQtyMode === 'full' ? moveDialog.batchQty : parseInt(moveQty, 10);
    if (!qty || qty <= 0 || qty > moveDialog.batchQty) {
      toast.error(`Enter 1–${moveDialog.batchQty}`);
      return;
    }
    const forward = moveDialog.direction === 'forward';
    const body = { quantity: qty };
    if (moveDialog.batchId) body.batch_id = moveDialog.batchId;
    if (forward) body.remainder = moveQtyMode === 'short' ? 'short' : 'hold';
    if (!forward) body.reason = moveBackReason || undefined;

    setMoveLoading(true);
    try {
      // the server reports what it actually did — a fixed sentence here used to
      // describe a split the backend was silently dropping
      const res = await api.post(
        `/job-cards/${jobCardId}/${forward ? 'move-forward' : 'move-backward'}`, body,
      );
      toast.success(res.data?.message || 'Stage moved');
      setMoveDialog(null);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to move stage');
    }
    setMoveLoading(false);
  };

  const receiveIntoStock = async () => {
    if (!stockConfirm) return;
    setMoveLoading(true);
    try {
      const res = await api.post(`/supervisor/batches/${stockConfirm.batchId}/pass-to-next-stage`, {
        qty_to_pass: stockConfirm.quantity,
      });
      toast.success(res.data?.message || `${stockConfirm.quantity} pcs are on the rack`);
      setStockConfirm(null);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to receive into stock');
    }
    setMoveLoading(false);
  };

  /**
   * Work handed over months ago still sits mid-pipeline because nobody was going
   * to walk it stage by stage after the fact. One press closes the lot.
   * Order-wide by design — the endpoint closes every item, so say so and make
   * them confirm rather than letting a stray thumb end a five-item order.
   */
  const completeOrder = async () => {
    const orderId = jobCard?.order_uuid || jobCard?.order_id;
    if (!orderId) { toast.error('Order not found'); return; }
    // a laggy handset turns one impatient double-press into two close-outs;
    // state is set too late to catch the second one
    if (completingRef.current) return;
    completingRef.current = true;
    setCompleting(true);
    try {
      const res = await api.post(`/orders/${orderId}/complete`);
      toast.success(res.data?.message || 'Order completed');
      setCompleteConfirm(false);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to complete order');
    }
    completingRef.current = false;
    setCompleting(false);
  };

  const orderPlate = async () => {
    setOrderingPlate(true);
    try {
      const res = await api.post(`/job-cards/${jobCardId}/plate/order`, { vendor: plateVendor.trim() });
      toast.success(res.data?.message || 'Plate marked ordered');
      setPlateSheet(false);
      setPlateVendor('');
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed');
    }
    setOrderingPlate(false);
  };

  const receivePlate = async () => {
    setReceivingPlate(true);
    try {
      const res = await api.post(`/job-cards/${jobCardId}/plate/receive`, {});
      toast.success(res.data?.message || 'Plate received');
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setReceivingPlate(false);
  };

  const openVendorSheet = async () => {
    setVendorSheet(true);
    try {
      const res = await api.get('/vendors/list');
      setVendors(res.data.vendors || []);
    } catch { setVendors([]); }
  };

  const sendToVendor = async () => {
    if (!vendorForm.vendor_id) { toast.error('Pick a vendor'); return; }
    setSendingVendor(true);
    try {
      await api.post('/vendors/send', {
        order_id: jobCard?.order_uuid || jobCard?.order_id,
        line_item_id: jobCard?.line_item_id || jobCardId,
        vendor_id: vendorForm.vendor_id,
        assignment_type: vendorForm.assignment_type,
        special_instructions: vendorForm.special_instructions || undefined,
        expected_return_date: vendorForm.expected_return_date || undefined,
      });
      toast.success('Job card sent to vendor');
      setVendorSheet(false);
      setVendorForm({
        vendor_id: '', assignment_type: 'binding_folding', special_instructions: '', expected_return_date: '',
      });
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send to vendor');
    }
    setSendingVendor(false);
  };

  const addRemark = async () => {
    if (!remarkText.trim()) return;
    const orderId = jobCard?.order_uuid || jobCard?.order_id;
    if (!orderId) { toast.error('Order not found'); return; }
    setAddingRemark(true);
    try {
      await api.put(`/orders/${orderId}/remarks`, { remark: remarkText.trim() });
      setRemarkText('');
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add remark');
    }
    setAddingRemark(false);
  };

  const printJobCard = async () => {
    if (!jobCard?.order_id) { toast.error('Order ID not found'); return; }
    setPrinting(true);
    try {
      await openAuthedFile(`/jobcard/${jobCard.order_id}/pdf`);
    } catch {
      toast.error('Failed to generate job card PDF');
    }
    setPrinting(false);
  };

  if (loading) return <ScreenLoader />;

  if (!jobCard) {
    return (
      <div className="flex flex-1 flex-col">
        <TopBar title="Job Card" onBack={() => navigate(-1)} />
        <div className="flex flex-1 flex-col items-center justify-center py-24">
          <FileText className="h-16 w-16" style={{ color: Colors.text.tertiary }} strokeWidth={1.2} />
          <p className="mt-4 text-[15px] font-medium" style={{ color: Colors.text.secondary }}>Job card not found</p>
        </div>
      </div>
    );
  }

  const status = productionStatus(jobCard);
  const StatusIcon = STATUS_ICONS[status] || Layers;
  // a finished job has no stage it is standing at, and "Stage 9 of 9" beside
  // COMPLETED reads as one still to run
  const showStage = !!currentStage && !['completed', 'delivered', 'stocked'].includes(status);
  // an order already closed out has nothing left to close, and the endpoint
  // answers a second attempt with a 400
  const showCompleteOrder = canCompleteOrder && !['delivered', 'stocked'].includes(status);
  const progress = stages.length > 0 ? Math.round((currentStageIndex / stages.length) * 100) : 0;
  const specs = jobCard.specifications || {};
  const remarks = Array.isArray(jobCard.remarks)
    ? jobCard.remarks
    : (jobCard.remarks ? [{ text: String(jobCard.remarks) }] : []);
  const stockRun = jobCard.order_kind === 'stock';

  return (
    <div className="flex flex-1 flex-col">
      <TopBar
        eyebrow={jobCard.order_id || 'JOB CARD'}
        title={jobCard.item_name || jobCard.product_type_name || 'Job Card'}
        onBack={() => navigate(-1)}
        actions={(
          <>
            {canPrint && (
              <IconButton icon={Printer} label="Print the job card" onClick={printJobCard} disabled={printing} testID="print-btn" />
            )}
            <IconButton icon={RefreshCw} label="Reload" onClick={fetchJobCard} testID="reload-btn" />
          </>
        )}
      />

      <div className="flex-1 px-4 pb-10">
        {/* Where the work stands */}
        <div
          className="mt-3 rounded-[18px] p-5"
          style={{ backgroundImage: `linear-gradient(135deg, ${Colors.accent.DEFAULT}, ${Colors.accent.light})` }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-white/80">Current Status</p>
              <p data-testid="job-card-status" className="mt-1 text-[22px] font-bold text-white">
                {(STATUS_LABELS[status] || status.replace(/_/g, ' ')).toUpperCase()}
              </p>
            </div>
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: STATUS_COLORS[status] || Colors.status.neutral }}
            >
              <StatusIcon className="h-5 w-5 text-white" />
            </span>
          </div>
          {showStage && (
            <div className="mt-3 border-t border-white/20 pt-3">
              <p className="text-[11px] font-medium uppercase tracking-[1px] text-white/80">Current Stage</p>
              <p className="mt-1 text-[17px] font-bold text-white">{currentStage.stage_name}</p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/20">
                <div className="h-full rounded-full bg-white" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-[11px] font-medium text-white/80">
                Stage {currentStageIndex + 1} of {stages.length}
              </p>
            </div>
          )}
        </div>

        {/* Supervisor action chips */}
        {(isSupervisor || showCompleteOrder) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {isSupervisor && (
              <>
                <button
                  type="button"
                  data-testid="send-to-vendor-chip"
                  onClick={openVendorSheet}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-[12px] font-semibold"
                  style={{
                    backgroundColor: Colors.background.primary,
                    borderColor: Colors.text.secondary,
                    color: Colors.text.secondary,
                  }}
                >
                  <Truck className="h-3.5 w-3.5" /> Send to Vendor
                </button>
                <button
                  type="button"
                  data-testid="edit-job-card-chip"
                  onClick={() => navigate(`/supervisor/job-card/${jobCardId}/edit`)}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-[12px] font-semibold"
                  style={{
                    backgroundColor: Colors.background.primary,
                    borderColor: Colors.accent.DEFAULT,
                    color: Colors.accent.DEFAULT,
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit Job Card
                </button>
              </>
            )}
            {/* last in the row and unfilled on purpose: it ends the whole order,
                so it should not be the chip a thumb lands on by accident */}
            {showCompleteOrder && (
              <button
                type="button"
                data-testid="complete-order-chip"
                onClick={() => setCompleteConfirm(true)}
                disabled={completing}
                className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-[12px] font-semibold disabled:opacity-50"
                style={{
                  backgroundColor: Colors.background.primary,
                  borderColor: tone('positive').border,
                  color: Colors.semantic.positive,
                }}
              >
                {completing ? <Spinner size="sm" /> : <CheckCheck className="h-3.5 w-3.5" />} Complete Order
              </button>
            )}
          </div>
        )}

        {/* Order Information — the order number and the product are already in
            the bar pinned above this screen, so the grid does not repeat them */}
        <div className="mt-3">
          <Section title="Order Information">
            <Panel>
              <div className="flex flex-wrap">
                <Fact icon={User} label="Customer" value={jobCard.firm_name || jobCard.customer_name || '—'} wide />
                {!!jobCard.firm_name && !!jobCard.customer_name && (
                  <Fact icon={User} label="Contact" value={jobCard.customer_name} />
                )}
                <Fact icon={Layers} label="Quantity" value={jobCard.quantity ? `${jobCard.quantity} pcs` : '—'} />
                <Fact icon={Tag} label="Type" value={jobCard.product_type_name || jobCard.order_type || '—'} />
                {!!jobCard.voice_number && <Fact icon={Phone} label="Voice #" value={jobCard.voice_number} />}
                {!!jobCard.expected_delivery_date && (
                  <Fact
                    icon={Calendar}
                    label="Delivery"
                    value={formatIST(jobCard.expected_delivery_date, { dateOnly: true })}
                  />
                )}
              </div>
            </Panel>
          </Section>
        </div>

        {/* Split type */}
        {!!jobCard.type_name && (
          <Section title="Split">
            <Panel style={{ backgroundColor: Colors.accent.wash }}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13px] font-medium" style={{ color: Colors.text.secondary }}>Type</span>
                <span className="text-[13px] font-semibold" style={{ color: Colors.text.primary }}>{jobCard.type_name}</span>
              </div>
              {!!jobCard.parent_job_card_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/supervisor/job-card/${jobCard.parent_job_card_id}`)}
                  className="pt-2 text-[13px] font-medium"
                  style={{ color: Colors.accent.DEFAULT }}
                >
                  View Parent Job Card →
                </button>
              )}
            </Panel>
          </Section>
        )}

        {/* Plate */}
        {needsPlate(jobCard) && (
          <Section title="Plate Status">
            <Panel style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: tone('caution').border }}>
              <div className="flex items-center justify-between gap-3">
                <StatusPill
                  testID="plate-status-pill"
                  label={(jobCard.plate_status || 'PENDING').toUpperCase()}
                  tone={jobCard.plate_status === 'received' ? 'positive'
                    : jobCard.plate_status === 'ordered' ? 'caution' : 'critical'}
                />
              </div>
              {!!jobCard.plate_ordered_vendor && (
                <p className="mt-2 text-[12px]" style={{ color: Colors.semantic.caution }}>
                  Vendor: {jobCard.plate_ordered_vendor}
                </p>
              )}
              {!!jobCard.plate_ordered_at && (
                <p className="text-[11px]" style={{ color: Colors.semantic.caution }}>
                  Ordered: {formatIST(jobCard.plate_ordered_at, { dateOnly: true })}
                </p>
              )}
              {!!jobCard.plate_received_at && (
                <p className="text-[11px]" style={{ color: Colors.semantic.positive }}>
                  Received: {formatIST(jobCard.plate_received_at, { dateOnly: true })}
                </p>
              )}
              {canManagePlate && (
                <div className="mt-3 flex gap-2">
                  {(!jobCard.plate_status || jobCard.plate_status === 'pending') && (
                    <button
                      type="button"
                      data-testid="plate-order-btn"
                      onClick={() => setPlateSheet(true)}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-[14px] py-2.5 text-[13px] font-semibold"
                      style={{ backgroundColor: Colors.status.warning, color: Colors.text.inverse }}
                    >
                      <Send className="h-3.5 w-3.5" /> Mark Ordered
                    </button>
                  )}
                  {jobCard.plate_status === 'ordered' && (
                    <button
                      type="button"
                      data-testid="plate-receive-btn"
                      onClick={receivePlate}
                      disabled={receivingPlate}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-[14px] py-2.5 text-[13px] font-semibold disabled:opacity-70"
                      style={{ backgroundColor: Colors.status.success, color: Colors.text.inverse }}
                    >
                      {receivingPlate
                        ? <Spinner size="sm" className="!border-white !border-b-transparent" />
                        : <><CheckCircle2 className="h-3.5 w-3.5" /> Mark Received</>}
                    </button>
                  )}
                </div>
              )}
            </Panel>
          </Section>
        )}

        {/* the one thing the card is opened to do */}
        {canMoveStages && jobCard.status !== 'completed' && (
          <Section title="Stage Controls">
            <Panel>
              {batches.length > 0 ? batches.map((batch, bIdx) => {
                if (batch.status === 'completed') return null;
                const batchStageIdx = batch.current_stage_index || 0;
                const atFirst = batchStageIdx <= 0;
                const atLast = batchStageIdx >= stages.length - 1;
                const prevName = !atFirst ? stages[batchStageIdx - 1]?.stage_name : null;
                const nextName = !atLast ? stages[batchStageIdx + 1]?.stage_name : null;
                // the button says which machine is on it, so the supervisor does
                // not have to open a picker to find out whether one is
                const onMachine = (batch.stage_assignments || []).find(
                  (a) => a.stage_name === batch.current_stage_name && a.machine_id,
                );
                return (
                  <div
                    key={batch.id || bIdx}
                    className="border-b py-3 last:border-b-0"
                    style={{ borderColor: Colors.border.subtle }}
                  >
                    <p className="text-[13px] font-bold" style={{ color: Colors.text.primary }}>
                      Batch #{batch.batch_number || bIdx + 1}
                    </p>
                    <p className="mt-0.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
                      @ {batch.current_stage_name}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <MoveButton
                        icon={Cog}
                        label={onMachine?.machine_name || 'Machine'}
                        disabled={moveLoading}
                        testID={`machine-${batch.batch_id || batch.id}`}
                        onClick={() => assignment.start({
                          batchId: batch.batch_id || batch.id,
                          stageName: batch.current_stage_name
                            || stages[batch.current_stage_index || 0]?.stage_name || '',
                          label: `Batch #${batch.batch_number || 1} · ${batch.quantity ?? jobCard.quantity ?? 0} pcs`,
                        })}
                      />
                      {!atFirst && (
                        <MoveButton
                          icon={ArrowLeft}
                          label={prevName}
                          disabled={moveLoading}
                          testID={`back-${batch.batch_id || batch.id}`}
                          onClick={() => handleMoveBackward(batch, batch.current_stage_name, prevName)}
                        />
                      )}
                      {!atLast && (
                        <MoveButton
                          icon={ArrowRight}
                          iconRight
                          label={nextName}
                          disabled={moveLoading}
                          testID={`forward-${batch.batch_id || batch.id}`}
                          onClick={() => handleMoveForward(batch)}
                        />
                      )}
                      {/* Stock In is the last stage of a replenishment run, and
                          the run closes when the store counts the goods in —
                          there is no dispatch behind it to do that. */}
                      {atLast && endsAtStockIn && (
                        <MoveButton
                          icon={Archive}
                          label="Receive into stock"
                          disabled={moveLoading}
                          testID="receive-into-stock-btn"
                          onClick={() => setStockConfirm({
                            batchId: batch.batch_id || batch.id,
                            quantity: batch.quantity,
                          })}
                        />
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="py-2">
                  <p className="text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
                    @ {currentStage?.stage_name} · {jobCard.quantity} pcs
                  </p>
                  <div className="mt-2 flex gap-2">
                    <MoveButton
                      icon={Cog}
                      label="Machine"
                      onClick={() => assignment.start({
                        batchId: jobCardId,
                        stageName: currentStage?.stage_name || '',
                        label: `${jobCard.quantity ?? 0} pcs`,
                      })}
                    />
                    {currentStageIndex > 0 && (
                      <MoveButton
                        icon={ArrowLeft}
                        label={stages[currentStageIndex - 1]?.stage_name}
                        onClick={() => handleMoveBackward(
                          null, currentStage?.stage_name, stages[currentStageIndex - 1]?.stage_name,
                        )}
                      />
                    )}
                    {currentStageIndex < stages.length - 1 && (
                      <MoveButton
                        icon={ArrowRight}
                        iconRight
                        label={stages[currentStageIndex + 1]?.stage_name}
                        onClick={() => handleMoveForward(null)}
                      />
                    )}
                  </div>
                </div>
              )}
              {moveLoading && <div className="pt-2"><Spinner size="sm" /></div>}
            </Panel>
          </Section>
        )}

        {/* Specifications */}
        {Object.keys(specs).length > 0 && (
          <Section
            title="Specifications"
            collapsible
            expanded={specsExpanded}
            onToggle={() => setSpecsExpanded(!specsExpanded)}
          >
            <Panel>
              {Object.entries(specs).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-start justify-between gap-3 border-b py-1.5 last:border-b-0"
                  style={{ borderColor: Colors.border.subtle }}
                >
                  <span className="flex-1 text-[13px] capitalize" style={{ color: Colors.text.secondary }}>
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span className="flex-1 text-right text-[13px] font-semibold" style={{ color: Colors.text.primary }}>
                    {Array.isArray(value) ? value.join(', ') : String(value || '—')}
                  </span>
                </div>
              ))}
              {isSupervisor && (
                <button
                  type="button"
                  data-testid="edit-specs-btn"
                  onClick={() => navigate(`/supervisor/job-card/${jobCardId}/edit`)}
                  className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-[14px] border py-2.5 text-[13px] font-semibold"
                  style={{
                    backgroundColor: Colors.accent.wash,
                    borderColor: Colors.accent.DEFAULT,
                    color: Colors.accent.DEFAULT,
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit Specifications
                </button>
              )}
            </Panel>
          </Section>
        )}

        {/* Production timeline */}
        <Section
          title="Production Timeline"
          collapsible
          expanded={timelineExpanded}
          onToggle={() => setTimelineExpanded(!timelineExpanded)}
        >
          <Panel>
            {stages.map((stage, idx) => {
              const done = idx < currentStageIndex;
              const current = idx === currentStageIndex;
              // any batch sent back marks the stage — reading only the first hid
              // every revisit on a split job's later runs
              const revisited = batches.some((b) => b.revisited_stages?.includes(stage.stage_name));
              return (
                <div key={stage.stage_name || idx} className="flex gap-4 pb-4 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2"
                      style={{
                        backgroundColor: done ? Colors.status.success
                          : current ? Colors.accent.DEFAULT : Colors.background.tertiary,
                        borderColor: done ? Colors.status.success
                          : current ? Colors.accent.DEFAULT : Colors.border.default,
                      }}
                    >
                      {done && <CheckCheck className="h-3 w-3 text-white" />}
                      {current && <span className="h-3 w-3 rounded-full bg-white" />}
                    </span>
                    {idx < stages.length - 1 && (
                      <span
                        className="mt-1 w-0.5 flex-1"
                        style={{ backgroundColor: done ? Colors.status.success : Colors.border.default }}
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="text-[15px]"
                        style={{
                          color: current ? Colors.accent.DEFAULT : Colors.text.secondary,
                          fontWeight: current ? 700 : 500,
                        }}
                      >
                        {stage.stage_name}
                      </span>
                      {revisited && <StatusPill label="Revisited" tone="caution" />}
                    </div>
                    {!!stage.completed_at && (
                      <p className="mt-0.5 text-[11px]" style={{ color: Colors.text.tertiary }}>
                        {formatIST(stage.completed_at, { compact: true })}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </Panel>
        </Section>

        {/* Remarks */}
        <Section
          title="Special Instructions"
          collapsible
          expanded={remarksExpanded}
          onToggle={() => setRemarksExpanded(!remarksExpanded)}
        >
          <Panel>
            {remarks.length > 0 ? (
              <div className="mb-3">
                {remarks.map((r, idx) => (
                  <div
                    key={idx}
                    className="border-b py-2 last:border-b-0"
                    style={{ borderColor: Colors.border.subtle }}
                  >
                    <p className="text-[13px]" style={{ color: Colors.text.primary }}>
                      {typeof r === 'string' ? r : (r.text || r.details || '')}
                    </p>
                    {(r.by || r.user_name) && (
                      <p className="mt-0.5 text-[11px]" style={{ color: Colors.text.tertiary }}>
                        — {r.by || r.user_name}{r.at ? `, ${formatIST(r.at, { compact: true })}` : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No remarks yet.</p>
            )}
            {isSupervisor && (
              <div className="mt-3 flex gap-2">
                <textarea
                  data-testid="remark-input"
                  className="min-h-[44px] flex-1 resize-none rounded-[14px] border px-3 py-2 text-[13px] outline-none"
                  style={{
                    backgroundColor: Colors.background.secondary,
                    borderColor: Colors.border.default,
                    color: Colors.text.primary,
                  }}
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  placeholder="Add a remark..."
                  aria-label="Add a remark"
                  rows={2}
                />
                <button
                  type="button"
                  data-testid="add-remark-btn"
                  onClick={addRemark}
                  disabled={!remarkText.trim() || addingRemark}
                  aria-label="Add the remark"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full disabled:opacity-40"
                  style={{ backgroundColor: Colors.accent.DEFAULT }}
                >
                  {addingRemark
                    ? <Spinner size="sm" className="!border-white !border-b-transparent" />
                    : <Send className="h-4 w-4 text-white" />}
                </button>
              </div>
            )}
          </Panel>
        </Section>

        {/* Attachments */}
        <Section
          title="Attachments"
          collapsible
          expanded={attachmentsExpanded}
          onToggle={() => setAttachmentsExpanded(!attachmentsExpanded)}
        >
          <Panel>
            <JobCardAttachments jobCardId={jobCardId} bare />
          </Panel>
        </Section>
      </div>

      {/* Move a batch */}
      <BottomSheet
        open={!!moveDialog}
        onClose={() => setMoveDialog(null)}
        testID="move-sheet"
        title={moveDialog?.direction === 'backward' ? 'Move Backward' : 'Move Forward'}
        subtitle={`From ${moveDialog?.fromStage} ${moveDialog?.direction === 'backward' ? 'back to' : 'to'} ${moveDialog?.toStage}`}
      >
        <div className="mb-3 flex gap-2">
          {(moveDialog?.direction === 'forward' ? ['full', 'short', 'partial'] : ['full', 'partial']).map((mode) => {
            const on = moveQtyMode === mode;
            return (
              <button
                key={mode}
                type="button"
                data-testid={`move-mode-${mode}`}
                onClick={() => {
                  setMoveQtyMode(mode);
                  setMoveQty(mode === 'full' ? String(moveDialog?.batchQty || 0) : '');
                }}
                className="flex-1 rounded-[10px] border py-2.5 text-[13px]"
                style={{
                  backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.secondary,
                  borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
                  color: on ? Colors.text.inverse : Colors.text.secondary,
                  fontWeight: on ? 600 : 500,
                }}
              >
                {mode === 'full' ? `Full (${moveDialog?.batchQty})` : mode === 'short' ? 'Short' : 'Split'}
              </button>
            );
          })}
        </div>

        {moveQtyMode !== 'full' && (
          <div className="mb-3">
            <p className="mb-1 text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
              {moveQtyMode === 'short'
                ? `Pcs actually moving on (of ${moveDialog?.batchQty}):`
                : `Quantity to move (of ${moveDialog?.batchQty} total):`}
            </p>
            <input
              data-testid="move-qty-input"
              className="h-11 w-full rounded-[10px] border px-3 text-[13px] outline-none"
              style={{
                backgroundColor: Colors.background.secondary,
                borderColor: Colors.border.default,
                color: Colors.text.primary,
              }}
              value={moveQty}
              onChange={(e) => setMoveQty(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              placeholder="Enter quantity"
              aria-label="Quantity to move"
            />
            <p
              className="mt-2 rounded-[10px] p-2 text-[11px]"
              style={{ backgroundColor: Colors.semantic.cautionWash, color: Colors.semantic.caution }}
            >
              {moveQtyMode === 'short'
                ? 'The rest is recorded as short at this stage. Nothing stays behind.'
                : 'The rest stays at this stage as its own batch.'}
            </p>
          </div>
        )}

        {moveDialog?.direction === 'backward' && (
          <>
            <p
              className="mb-2 rounded-[10px] p-2 text-[11px]"
              style={{ backgroundColor: Colors.semantic.cautionWash, color: Colors.semantic.caution }}
            >
              This is a correction/override. The stage will be marked as “revisited”.
            </p>
            <textarea
              data-testid="move-back-reason"
              className="mb-3 min-h-[44px] w-full resize-none rounded-[10px] border px-3 py-2 text-[13px] outline-none"
              style={{
                backgroundColor: Colors.background.secondary,
                borderColor: Colors.border.default,
                color: Colors.text.primary,
              }}
              value={moveBackReason}
              onChange={(e) => setMoveBackReason(e.target.value)}
              placeholder="Reason for moving back (optional)"
              aria-label="Reason for moving back"
            />
          </>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setMoveDialog(null)}
            className="flex-1 rounded-[10px] border py-3 text-[13px] font-medium"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.secondary,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-move"
            onClick={confirmMove}
            disabled={moveLoading}
            className="flex flex-1 items-center justify-center rounded-[10px] py-3 text-[13px] font-semibold disabled:opacity-60"
            style={{
              backgroundColor: moveDialog?.direction === 'forward' ? Colors.status.success : Colors.accent.DEFAULT,
              color: Colors.text.inverse,
            }}
          >
            {moveLoading
              ? <Spinner size="sm" className="!border-white !border-b-transparent" />
              : (moveDialog?.direction === 'backward' ? 'Move Back' : 'Move Forward')}
          </button>
        </div>
      </BottomSheet>

      {/* Plate vendor */}
      <BottomSheet
        open={plateSheet}
        onClose={() => setPlateSheet(false)}
        testID="plate-sheet"
        title="Mark Plate Ordered"
        subtitle="Who is making the plate?"
      >
        <input
          data-testid="plate-vendor-input"
          className="mb-3 h-11 w-full rounded-[10px] border px-3 text-[13px] outline-none"
          style={{
            backgroundColor: Colors.background.secondary,
            borderColor: Colors.border.default,
            color: Colors.text.primary,
          }}
          value={plateVendor}
          onChange={(e) => setPlateVendor(e.target.value)}
          placeholder="Vendor name (optional)"
          aria-label="Vendor name"
        />
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setPlateSheet(false)}
            className="flex-1 rounded-[10px] border py-3 text-[13px] font-medium"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.secondary,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="plate-vendor-confirm"
            onClick={orderPlate}
            disabled={orderingPlate}
            className="flex flex-1 items-center justify-center rounded-[10px] py-3 text-[13px] font-semibold disabled:opacity-60"
            style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
          >
            {orderingPlate
              ? <Spinner size="sm" className="!border-white !border-b-transparent" />
              : 'Mark Ordered'}
          </button>
        </div>
      </BottomSheet>

      {/* Send to vendor */}
      <BottomSheet
        open={vendorSheet}
        onClose={() => setVendorSheet(false)}
        testID="vendor-sheet"
        title="Send to Vendor"
        subtitle="Send this job card for external processing"
      >
        <label className="mb-1 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
          Vendor *
          <select
            data-testid="vendor-select"
            className="mt-1 h-11 w-full rounded-[10px] border px-3 text-[13px] outline-none"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.primary,
            }}
            value={vendorForm.vendor_id}
            onChange={(e) => setVendorForm((p) => ({ ...p, vendor_id: e.target.value }))}
          >
            <option value="">Select vendor…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.full_name || v.name}</option>
            ))}
          </select>
        </label>

        <label className="mb-1 mt-3 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
          Assignment Type
          <select
            data-testid="assignment-type-select"
            className="mt-1 h-11 w-full rounded-[10px] border px-3 text-[13px] outline-none"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.primary,
            }}
            value={vendorForm.assignment_type}
            onChange={(e) => setVendorForm((p) => ({ ...p, assignment_type: e.target.value }))}
          >
            {ASSIGNMENT_TYPES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </label>

        <label className="mb-1 mt-3 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
          Expected back (optional)
          <input
            data-testid="vendor-return-date"
            type="date"
            className="mt-1 h-11 w-full rounded-[10px] border px-3 text-[13px] outline-none"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.primary,
            }}
            value={vendorForm.expected_return_date}
            onChange={(e) => setVendorForm((p) => ({ ...p, expected_return_date: e.target.value }))}
          />
        </label>

        <label className="mb-3 mt-3 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>
          Instructions (optional)
          <textarea
            data-testid="vendor-instructions"
            className="mt-1 min-h-[44px] w-full resize-none rounded-[10px] border px-3 py-2 text-[13px] outline-none"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.primary,
            }}
            value={vendorForm.special_instructions}
            onChange={(e) => setVendorForm((p) => ({ ...p, special_instructions: e.target.value }))}
            rows={2}
          />
        </label>

        <button
          type="button"
          data-testid="confirm-send-vendor"
          onClick={sendToVendor}
          disabled={sendingVendor || !vendorForm.vendor_id}
          className="flex h-12 w-full items-center justify-center rounded-[10px] text-[15px] font-bold disabled:opacity-50"
          style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
        >
          {sendingVendor
            ? <Spinner size="sm" className="!border-white !border-b-transparent" />
            : 'Send to Vendor'}
        </button>
      </BottomSheet>

      <MachinePicker
        open={!!assignment.target}
        onClose={assignment.cancel}
        stageName={assignment.target?.stageName || ''}
        subtitle={assignment.target?.label}
        machines={assignment.machines}
        loadingMachines={assignment.loading}
        assigning={assignment.assigning}
        onAssign={assignment.pick}
      />

      <ConfirmDialog
        open={completeConfirm}
        title="Complete this order?"
        message={`Every item on ${jobCard.order_id} will be closed at whatever stage it has reached, and the order marked ${stockRun ? 'stocked' : 'delivered'}.\n\nThis covers the whole order, not just this job card.`}
        confirmLabel="Complete"
        cancelLabel="Cancel"
        destructive
        busy={completing}
        onConfirm={completeOrder}
        onCancel={() => setCompleteConfirm(false)}
      />

      <ConfirmDialog
        open={!!stockConfirm}
        title="Receive into stock"
        message={`Add ${stockConfirm?.quantity} pcs to the catalogue stock for this item?\n\nThe run closes here — a stock run is never dispatched.`}
        confirmLabel="Receive"
        cancelLabel="Cancel"
        busy={moveLoading}
        onConfirm={receiveIntoStock}
        onCancel={() => setStockConfirm(null)}
      />
    </div>
  );
}
